import { useState } from "react";
import { useLocation } from "wouter";
import { CalendarDays } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useCreateEvent } from "@/lib/queries";
import { ApiError } from "@/lib/api";

export default function NewEvent() {
  const [, setLocation] = useLocation();
  const create = useCreateEvent();
  const [name, setName] = useState("");
  const [format, setFormat] = useState<"singles" | "doubles" | "mixed">("doubles");
  const [startDate, setStartDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [endDate, setEndDate] = useState("");
  const [location, setLoc] = useState("");
  const [description, setDescription] = useState("");
  const [isPublic, setIsPublic] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setError(null);
    if (!name.trim()) return setError("Name is required");
    try {
      const created = await create.mutateAsync({
        name: name.trim(),
        startDate,
        endDate: endDate || null,
        location: location.trim() || null,
        description: description.trim() || null,
        format,
        isPublic,
      });
      setLocation(`/dashboard/events/${created.slug}`);
    } catch (e) {
      setError((e as ApiError).message ?? "Could not create event");
    }
  }

  return (
    <div className="px-4 sm:px-8 py-6 sm:py-10 max-w-2xl mx-auto space-y-6">
      <header>
        <h1 className="text-2xl sm:text-3xl font-display uppercase tracking-tight inline-flex items-center gap-2">
          <CalendarDays size={22} /> New event
        </h1>
        <p className="text-muted-foreground text-sm">
          Create the container first. You can register players and seed
          brackets from the detail page once it's live.
        </p>
      </header>

      <Card>
        <CardHeader>
          <CardTitle>Details</CardTitle>
          <CardDescription>
            All fields except name and start date are optional.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <Field label="Name">
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Spring Kickoff 2026"
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Start date">
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </Field>
            <Field label="End date (optional)">
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </Field>
          </div>

          <Field label="Format">
            <div className="flex gap-2">
              {(["doubles", "singles", "mixed"] as const).map((f) => (
                <button
                  key={f}
                  onClick={() => setFormat(f)}
                  className={`px-3 py-2 rounded-md text-xs font-display uppercase tracking-widest border flex-1 ${
                    format === f
                      ? "bg-foreground text-background border-foreground"
                      : "border-border text-muted-foreground hover:text-foreground"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </Field>

          <Field label="Location (optional)">
            <Input
              value={location}
              onChange={(e) => setLoc(e.target.value)}
              placeholder="Maplewood Park"
            />
          </Field>

          <button
            type="button"
            onClick={() => setIsPublic((v) => !v)}
            className="w-full flex items-center justify-between p-3 rounded-md border border-border hover:bg-muted/50 text-left"
          >
            <span className="text-sm">
              <span className="font-medium">List on /events</span>
              <span className="block text-xs text-muted-foreground">
                Cross-org discovery + SEO meta. You can change this later.
              </span>
            </span>
            <span
              className={`inline-flex items-center text-xs font-display uppercase tracking-widest ${
                isPublic ? "text-primary" : "text-muted-foreground"
              }`}
            >
              {isPublic ? "Public" : "Private"}
            </span>
          </button>

          <Field label="Description (optional)">
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              placeholder="Double-elim, BYO board, $10 entry."
            />
          </Field>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => setLocation("/dashboard/events")}
            >
              Cancel
            </Button>
            <Button onClick={submit} disabled={create.isPending}>
              {create.isPending ? "Creating…" : "Create event"}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium">{label}</label>
      {children}
    </div>
  );
}
